import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import EnrollPictureScreen from '../../components/EnrollPictureScreen/EnrollPictureScreen';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { FormGrid } from '../../components/FormField/FormGrid';
import { useToast } from '../../components/Toast/ToastContext';
import { SUPPORT_EMAIL } from '../../lib/emails';
import { US_STATES } from '../../lib/utils';
import {
  registerChipResellerV2Mutation,
  RegisterChipResellerV2Data,
  RegisterChipResellerV2Input,
  rescueOrgInfoQuery,
  RescueOrgInfoData,
} from '../../lib/rescueQueries';
import type { AppState } from '../../../lib/store';
import nanoProduct from '../../assets/es-nano-product.jpg';
import nanoDefense from '../../assets/es-nano-defense.jpg';
import sharedStyles from '../../components/EnrollPictureScreen/EnrollPictureScreen.module.scss';
import orderStyles from './Chips.module.scss';
import styles from './ChipsEnrollModal.module.scss';

interface ChipsEnrollModalProps {
  open: boolean;
  onClose: () => void;
}

/** Pragmatic email shape check — backend remains authoritative. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Enrollment flow for the Fi Nano microchip program.
 *
 *   Step 1 — product hero + program pitch
 *   Step 2 — pairing pitch (Nano as a backup for GPS-tracked dogs)
 *   Step 3 — application form (contact, org address, shipping address)
 *
 * Submitting calls the existing `registerChipResellerV2` mutation with `types: ['RESCUE']`, which
 * creates a RESCUE chip reseller (Accepted, unverified) for the signed-in rescue user and enrolls
 * them in the rescue org group. The reseller name is the rescue household name. Contact/name/phone
 * fields are pre-filled from the rescue user account. Fi verifies new rescues before chips unlock.
 */
export default function ChipsEnrollModal({ open, onClose }: ChipsEnrollModalProps) {
  const session = useSelector((state: AppState) => state.session);
  const { showToast } = useToast();
  // Shares the cache with RescueContext's query, so this adds no extra network request.
  const { data: orgData } = useQuery<RescueOrgInfoData>(rescueOrgInfoQuery, { errorPolicy: 'all' });

  const user = orgData?.currentUser;
  const rescueHouseholdName =
    user?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE')?.household.name?.trim() ?? '';

  // The modal only renders after the page's org query has resolved (it gates on orgInfoLoading), so
  // these values are available for the field initialisers below — no seeding effect needed.
  const prefill = {
    firstName: user?.firstName ?? '',
    lastName: user?.lastName ?? '',
    email: user?.email ?? session?.email ?? '',
    phone: user?.phoneNumber ?? '',
  };

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Contact
  const [firstName, setFirstName] = useState(prefill.firstName);
  const [lastName, setLastName] = useState(prefill.lastName);
  const [email, setEmail] = useState(prefill.email);
  const [phone, setPhone] = useState(prefill.phone);
  const [website, setWebsite] = useState('');
  // Organization address
  const [orgStreet, setOrgStreet] = useState('');
  const [orgApt, setOrgApt] = useState('');
  const [orgCity, setOrgCity] = useState('');
  const [orgState, setOrgState] = useState('');
  const [orgZip, setOrgZip] = useState('');
  // Shipping address
  const [sameAsOrg, setSameAsOrg] = useState(true);
  const [shipStreet, setShipStreet] = useState('');
  const [shipApt, setShipApt] = useState('');
  const [shipCity, setShipCity] = useState('');
  const [shipState, setShipState] = useState('');
  const [shipZip, setShipZip] = useState('');

  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const [registerReseller, { loading }] = useMutation<
    RegisterChipResellerV2Data,
    { input: RegisterChipResellerV2Input }
  >(registerChipResellerV2Mutation, {
    refetchQueries: [{ query: rescueOrgInfoQuery }],
    awaitRefetchQueries: true,
  });

  function resetForm() {
    setStep(1);
    setFirstName(prefill.firstName);
    setLastName(prefill.lastName);
    setEmail(prefill.email);
    setPhone(prefill.phone);
    setWebsite('');
    setOrgStreet('');
    setOrgApt('');
    setOrgCity('');
    setOrgState('');
    setOrgZip('');
    setSameAsOrg(true);
    setShipStreet('');
    setShipApt('');
    setShipCity('');
    setShipState('');
    setShipZip('');
    setShowErrors(false);
    setSubmitError(null);
  }

  function handleClose() {
    if (submittingRef.current) {
      return;
    }
    resetForm();
    onClose();
  }

  const trimmedEmail = email.trim();
  const errors = {
    firstName: firstName.trim() === '',
    emailMissing: trimmedEmail === '',
    emailInvalid: trimmedEmail !== '' && !EMAIL_RE.test(trimmedEmail),
    orgStreet: orgStreet.trim() === '',
    orgCity: orgCity.trim() === '',
    orgState: orgState === '',
    orgZip: orgZip.trim() === '',
    shipStreet: !sameAsOrg && shipStreet.trim() === '',
    shipCity: !sameAsOrg && shipCity.trim() === '',
    shipState: !sameAsOrg && shipState === '',
    shipZip: !sameAsOrg && shipZip.trim() === '',
  };
  const hasErrors = Object.values(errors).some(Boolean);

  async function handleSubmit() {
    if (submittingRef.current) {
      return;
    }
    if (hasErrors) {
      setSubmitError(null);
      setShowErrors(true);
      return;
    }
    submittingRef.current = true;
    setSubmitError(null);
    try {
      const orgAddress = {
        line1: orgStreet.trim(),
        line2: orgApt.trim() || null,
        city: orgCity.trim(),
        state: orgState,
        country: 'US',
        zip: orgZip.trim(),
      };
      const shippingAddress = sameAsOrg
        ? orgAddress
        : {
            line1: shipStreet.trim(),
            line2: shipApt.trim() || null,
            city: shipCity.trim(),
            state: shipState,
            country: 'US',
            zip: shipZip.trim(),
          };
      await registerReseller({
        variables: {
          input: {
            name: rescueHouseholdName,
            types: ['RESCUE'],
            petInterestTypes: [],
            contactInfo: {
              firstName: firstName.trim(),
              lastName: lastName.trim() || null,
              primaryEmail: trimmedEmail,
              primaryPhone: phone.trim() || null,
              website: website.trim() || null,
              line1: orgAddress.line1,
              line2: orgAddress.line2,
              city: orgAddress.city,
              state: orgAddress.state,
              zipcode: orgAddress.zip,
              country: 'US',
            },
            shippingAddress,
          },
        },
      });
      showToast("You're set up for the Fi Nano program. We'll verify your rescue, then your chips unlock.");
      resetForm();
      onClose();
    } catch (err: any) {
      const msg = err?.graphQLErrors?.[0]?.message || err?.message || 'Something went wrong';
      setSubmitError(`Couldn't complete enrollment: ${msg} — questions? Email ${SUPPORT_EMAIL}`);
    } finally {
      submittingRef.current = false;
    }
  }

  const isForm = step === 3;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size={isForm ? 'wide' : 'extra-wide'}
      className={isForm ? undefined : sharedStyles.modal}
      backdropClassName={isForm ? undefined : sharedStyles.backdrop}
      showCloseX={isForm}
    >
      {step === 1 && (
        <EnrollPictureScreen
          heroSrc={nanoProduct}
          heroAlt="Fi Nano microchip product"
          onClose={handleClose}
          title="Add microchipping to your rescue program"
          description="Fi Nano chips are free for rescue partners. ISO certified, ICAR registered, and compatible with all scanners. Free to register, free to update, searchable 24/7."
          currentStep={1}
          totalSteps={3}
          highlightCurrentOnly
          actions={
            <Button variant="dark" onClick={() => setStep(2)}>
              Next &rarr;
            </Button>
          }
        />
      )}

      {step === 2 && (
        <EnrollPictureScreen
          heroSrc={nanoDefense}
          heroAlt="Dog with Fi collar outdoors"
          onClose={handleClose}
          title="Your last line of defense"
          description="You already track dogs with GPS. Nano is the permanent backup when a collar battery dies or a device is removed. Together, they keep every pet findable."
          currentStep={2}
          totalSteps={3}
          highlightCurrentOnly
          actions={
            <>
              <Button variant="secondary" onClick={() => setStep(1)}>
                &larr; Back
              </Button>
              <Button variant="dark" onClick={() => setStep(3)}>
                Next &rarr;
              </Button>
            </>
          }
        />
      )}

      {step === 3 && (
        <>
          <ModalTitle>Enroll in the Fi Nano Microchip program</ModalTitle>
          <p className={orderStyles.modalDescription}>
            Setting up Fi Nano for <strong>{rescueHouseholdName || 'your rescue'}</strong>. Fi verifies all new
            applications.
          </p>

          <div className={orderStyles.orderSectionHeading}>Contact</div>
          <FormGrid>
            <PortalField
              label="First Name *"
              error="Please provide a first name"
              showError={showErrors && errors.firstName}
            >
              <PortalInput
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                hasError={showErrors && errors.firstName}
              />
            </PortalField>
            <PortalField label="Last Name">
              <PortalInput value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" />
            </PortalField>
            <PortalField
              label="Email *"
              error={errors.emailMissing ? 'Please provide an email' : 'Please provide a valid email address'}
              showError={showErrors && (errors.emailMissing || errors.emailInvalid)}
            >
              <PortalInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.org"
                hasError={showErrors && (errors.emailMissing || errors.emailInvalid)}
              />
            </PortalField>
            <PortalField label="Primary Phone">
              <PortalInput
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-5555"
              />
            </PortalField>
            <PortalField label="Website" fullWidth>
              <PortalInput
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://yourrescue.org"
              />
            </PortalField>
          </FormGrid>

          <div className={orderStyles.orderSectionHeading}>Organization Address</div>
          <FormGrid>
            <PortalField
              label="Street Address *"
              fullWidth
              error="Please provide a street address"
              showError={showErrors && errors.orgStreet}
            >
              <PortalInput
                value={orgStreet}
                onChange={(e) => setOrgStreet(e.target.value)}
                placeholder="123 Main Street"
                hasError={showErrors && errors.orgStreet}
              />
            </PortalField>
            <PortalField label="Apt / Suite">
              <PortalInput value={orgApt} onChange={(e) => setOrgApt(e.target.value)} placeholder="Apt / Suite" />
            </PortalField>
            <PortalField label="City *" error="Please provide a city" showError={showErrors && errors.orgCity}>
              <PortalInput
                value={orgCity}
                onChange={(e) => setOrgCity(e.target.value)}
                placeholder="City"
                hasError={showErrors && errors.orgCity}
              />
            </PortalField>
            <PortalField label="State *" error="Please select a state" showError={showErrors && errors.orgState}>
              <PortalSelect
                value={orgState}
                onChange={(e) => setOrgState(e.target.value)}
                hasError={showErrors && errors.orgState}
              >
                <option value="">Select…</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </PortalSelect>
            </PortalField>
            <PortalField label="Zip Code *" error="Please provide a zip code" showError={showErrors && errors.orgZip}>
              <PortalInput
                value={orgZip}
                onChange={(e) => setOrgZip(e.target.value)}
                placeholder="ZIP"
                inputMode="numeric"
                hasError={showErrors && errors.orgZip}
              />
            </PortalField>
          </FormGrid>

          <div className={orderStyles.orderSectionHeading}>Shipping Address</div>
          <button
            type="button"
            className={styles.sameAsRow}
            onClick={() => setSameAsOrg((v) => !v)}
            aria-pressed={sameAsOrg}
          >
            <span
              className={`${styles.sameAsCheckbox} ${sameAsOrg ? styles.sameAsCheckboxChecked : ''}`}
              aria-hidden="true"
            >
              {sameAsOrg && (
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M3.5 8.5L6.5 11.5L12.5 4.5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span className={styles.sameAsLabel}>Same as organization address</span>
          </button>

          {!sameAsOrg && (
            <FormGrid>
              <PortalField
                label="Street Address *"
                fullWidth
                error="Please provide a street address"
                showError={showErrors && errors.shipStreet}
              >
                <PortalInput
                  value={shipStreet}
                  onChange={(e) => setShipStreet(e.target.value)}
                  placeholder="123 Main Street"
                  hasError={showErrors && errors.shipStreet}
                />
              </PortalField>
              <PortalField label="Apt / Suite">
                <PortalInput value={shipApt} onChange={(e) => setShipApt(e.target.value)} placeholder="Apt / Suite" />
              </PortalField>
              <PortalField label="City *" error="Please provide a city" showError={showErrors && errors.shipCity}>
                <PortalInput
                  value={shipCity}
                  onChange={(e) => setShipCity(e.target.value)}
                  placeholder="City"
                  hasError={showErrors && errors.shipCity}
                />
              </PortalField>
              <PortalField label="State *" error="Please select a state" showError={showErrors && errors.shipState}>
                <PortalSelect
                  value={shipState}
                  onChange={(e) => setShipState(e.target.value)}
                  hasError={showErrors && errors.shipState}
                >
                  <option value="">Select…</option>
                  {US_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </PortalSelect>
              </PortalField>
              <PortalField
                label="Zip Code *"
                error="Please provide a zip code"
                showError={showErrors && errors.shipZip}
              >
                <PortalInput
                  value={shipZip}
                  onChange={(e) => setShipZip(e.target.value)}
                  placeholder="ZIP"
                  inputMode="numeric"
                  hasError={showErrors && errors.shipZip}
                />
              </PortalField>
            </FormGrid>
          )}

          {submitError && <div className={orderStyles.errorContainer}>{submitError}</div>}

          <ModalActions>
            <Button variant="secondary" onClick={() => setStep(2)} disabled={loading}>
              &larr; Back
            </Button>
            <Button variant="green" onClick={handleSubmit} disabled={loading}>
              {loading ? 'Setting up…' : 'Enroll'}
            </Button>
          </ModalActions>
        </>
      )}
    </Modal>
  );
}
